import { Emitter } from './Emitter';

export interface ViewportInfo {
  width: number;
  height: number;
  dpr: number;
  orientation: 'landscape' | 'portrait';
  /** Narrow screens (phones) get compact layouts. */
  compact: boolean;
}

/** Tracks the CSS viewport and coalesces resize events into one per animation frame. */
export class Viewport extends Emitter<{ resize: ViewportInfo }> {
  info: ViewportInfo;
  private pending = 0;
  private dprCap = 2;

  constructor() {
    super();
    this.info = this.measure();
    window.addEventListener('resize', this.schedule);
    window.visualViewport?.addEventListener('resize', this.schedule);
  }

  setDprCap(cap: number): void {
    this.dprCap = cap;
    this.schedule();
  }

  private measure(): ViewportInfo {
    const width = Math.max(1, Math.round(window.visualViewport?.width ?? window.innerWidth));
    const height = Math.max(1, Math.round(window.visualViewport?.height ?? window.innerHeight));
    const dpr = Math.min(this.dprCap ?? 2, Math.max(1, window.devicePixelRatio || 1));
    const orientation = width >= height ? 'landscape' : 'portrait';
    return { width, height, dpr, orientation, compact: width < 760 || height < 480 };
  }

  private schedule = (): void => {
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = 0;
      this.info = this.measure();
      document.documentElement.dataset.orientation = this.info.orientation;
      document.documentElement.classList.toggle('compact', this.info.compact);
      this.emit('resize', this.info);
    });
  };

  /** Force a resize notification (e.g. after a DOM layout change). */
  refresh(): void {
    this.schedule();
  }
}
